import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import type {
  AddTeamAssistantChannelExclusionRequest,
  AddTeamAssistantChannelExclusionResponse,
  ListTeamAssistantAvailableChannelsResponse,
  ListTeamAssistantChannelExclusionsResponse,
  RemoveTeamAssistantChannelExclusionResponse,
  TeamAssistant,
  TeamAssistantAvailableChannel,
  TeamAssistantChannelExclusion,
} from '@dreamai/types';
import { apiErrorMessage } from './organisationLabels';

function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

export type ChannelsFeedback = {
  type: 'success' | 'error';
  message: string;
};

interface TeamAssistantChannelsProps {
  /** `/organisations/:id/team-assistants` */
  base: string;
  assistant: TeamAssistant;
  /** Whether the parent panel has an action in flight */
  disabled: boolean;
  authHeaders: () => Promise<{ headers: { Authorization: string } }>;
  onFeedback: (feedback: ChannelsFeedback | null) => void;
}

const LockIcon: FC<{ title: string }> = ({ title }) => (
  <svg
    className='team-assistant-channel-lock'
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    role='img'
    aria-label={title}
  >
    <rect x='4' y='11' width='16' height='10' rx='2' />
    <path d='M8 11V7a4 4 0 0 1 8 0v4' />
  </svg>
);

const HashIcon: FC = () => (
  <svg
    className='team-assistant-channel-hash'
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <line x1='4' y1='9' x2='20' y2='9' />
    <line x1='4' y1='15' x2='20' y2='15' />
    <line x1='10' y1='3' x2='8' y2='21' />
    <line x1='16' y1='3' x2='14' y2='21' />
  </svg>
);

export const channelLabel = (
  exclusion: Pick<
    TeamAssistantChannelExclusion,
    'channelName' | 'externalChannelId'
  >,
) => exclusion.channelName ?? exclusion.externalChannelId;

interface ChannelPickerProps {
  channels: TeamAssistantAvailableChannel[] | null;
  error: string | null;
  excludedIds: Set<string>;
  adding: string | null;
  disabled: boolean;
  onPick: (channel: TeamAssistantAvailableChannel) => void;
  onClose: () => void;
}

const ChannelPicker: FC<ChannelPickerProps> = ({
  channels,
  error,
  excludedIds,
  adding,
  disabled,
  onPick,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const candidates = useMemo(
    () =>
      (channels ?? [])
        .filter((c) => !excludedIds.has(c.externalChannelId))
        .filter((c) => !needle || c.name.toLowerCase().includes(needle))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [channels, excludedIds, needle],
  );

  return (
    <div
      className='team-assistant-channel-picker'
      data-testid='team-assistant-channel-picker'
    >
      <div className='organisation-field-row'>
        <input
          type='search'
          className='field-input'
          aria-label='Search channels'
          placeholder='Search channels'
          value={query}
          autoFocus
          disabled={channels === null && !error}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type='button'
          className='btn-secondary'
          disabled={adding !== null}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className='organisation-feedback error' role='alert'>
          {error}
        </p>
      )}
      {channels === null && !error && (
        <p className='organisation-empty'>Loading channels…</p>
      )}
      {channels && candidates.length === 0 && (
        <p className='organisation-empty'>
          {needle
            ? 'No channel matches that search.'
            : channels.length === 0
              ? 'The secretary cannot see any channels in the workspace yet.'
              : 'Every channel the secretary can see is already listed.'}
        </p>
      )}
      {candidates.length > 0 && (
        <ul className='team-assistant-channel-options' role='listbox'>
          {candidates.map((channel) => {
            const busy = adding === channel.externalChannelId;
            return (
              <li key={channel.externalChannelId} role='option'>
                <button
                  type='button'
                  className='team-assistant-channel-option'
                  disabled={disabled || adding !== null}
                  onClick={() => onPick(channel)}
                  data-testid='team-assistant-channel-option'
                >
                  <span className='team-assistant-channel-name'>
                    {channel.isPrivate ? (
                      <LockIcon title='Private channel' />
                    ) : (
                      <HashIcon />
                    )}
                    {channel.name}
                  </span>
                  <span className='team-assistant-meta'>
                    {busy
                      ? 'Adding…'
                      : channel.isPrivate
                        ? 'Private channel'
                        : 'Public channel'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * The "Channels" sub-card of a connected Team Assistant: channels it must
 * ignore, plus an "Exclude channel" picker fed by channels the workspace lets
 * the assistant see.
 */
export const TeamAssistantChannels: FC<TeamAssistantChannelsProps> = ({
  base,
  assistant,
  disabled,
  authHeaders,
  onFeedback,
}) => {
  const [exclusions, setExclusions] = useState<
    TeamAssistantChannelExclusion[] | null
  >(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [available, setAvailable] = useState<
    TeamAssistantAvailableChannel[] | null
  >(null);
  const [availableError, setAvailableError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const exclusionsUrl = `${base}/${assistant.id}/channel-exclusions`;
  const workspaceId = assistant.installation?.externalWorkspaceId ?? null;

  useEffect(() => {
    let cancelled = false;
    setExclusions(null);
    setLoadError(null);
    setPicking(false);
    (async () => {
      try {
        const { data } =
          await api.get<ListTeamAssistantChannelExclusionsResponse>(
            exclusionsUrl,
            await authHeaders(),
          );
        if (!cancelled) setExclusions(data.exclusions);
      } catch (err) {
        if (!cancelled && httpStatus(err) !== 404) {
          console.error('Error loading team assistant exclusions:', err);
          setLoadError(
            apiErrorMessage(err, 'Could not load excluded channels.'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Reload when the assistant is connected to a different workspace.
  }, [exclusionsUrl, workspaceId, authHeaders]);

  const loadAvailable = useCallback(async () => {
    setAvailable(null);
    setAvailableError(null);
    try {
      const { data } =
        await api.get<ListTeamAssistantAvailableChannelsResponse>(
          `${base}/${assistant.id}/available-channels`,
          await authHeaders(),
        );
      setAvailable(data.channels);
    } catch (err) {
      console.error('Error loading available channels:', err);
      setAvailableError(
        apiErrorMessage(err, 'Could not load the channels of the workspace.'),
      );
    }
  }, [base, assistant.id, authHeaders]);

  const openPicker = () => {
    onFeedback(null);
    setPicking(true);
    void loadAvailable();
  };

  const handleAdd = async (channel: TeamAssistantAvailableChannel) => {
    onFeedback(null);
    setBusyKey(`add:${channel.externalChannelId}`);
    try {
      const body: AddTeamAssistantChannelExclusionRequest = {
        externalChannelId: channel.externalChannelId,
      };
      const { data } = await api.post<AddTeamAssistantChannelExclusionResponse>(
        exclusionsUrl,
        body,
        await authHeaders(),
      );
      if (!data.success) throw new Error();
      setExclusions((prev) => [...(prev ?? []), data.exclusion]);
      setPicking(false);
      onFeedback({
        type: 'success',
        message: `#${channelLabel(data.exclusion)} excluded.`,
      });
    } catch (err) {
      console.error('Could not add the channel.', err);
      onFeedback({
        type: 'error',
        message: apiErrorMessage(err, 'Could not add the channel.'),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (exclusion: TeamAssistantChannelExclusion) => {
    onFeedback(null);
    setBusyKey(`remove:${exclusion.id}`);
    try {
      const { data } =
        await api.delete<RemoveTeamAssistantChannelExclusionResponse>(
          `${exclusionsUrl}/${exclusion.id}`,
          await authHeaders(),
        );
      if (!data.success) throw new Error();
      setExclusions((prev) =>
        prev ? prev.filter((item) => item.id !== exclusion.id) : prev,
      );
      onFeedback({
        type: 'success',
        message: `#${channelLabel(exclusion)} is no longer excluded.`,
      });
    } catch (err) {
      console.error('Could not remove the channel.', err);
      onFeedback({
        type: 'error',
        message: apiErrorMessage(err, 'Could not remove the channel.'),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const busy = disabled || busyKey !== null;
  const adding = busyKey?.startsWith('add:')
    ? busyKey.slice('add:'.length)
    : null;
  const excludedIds = useMemo(
    () => new Set((exclusions ?? []).map((item) => item.externalChannelId)),
    [exclusions],
  );

  return (
    <div className='organisation-subcard' data-testid='team-assistant-channels'>
      <h4 className='organisation-subcard-title'>Channels</h4>
      <p className='organisation-card-text'>
        {assistant.name} answers in all public channels. To use it in a private
        channel, invite @{assistant.name} there.
      </p>
      <h5 className='organisation-subcard-title'>Excluded channels</h5>
      {loadError && (
        <p className='organisation-feedback error' role='alert'>
          {loadError}
        </p>
      )}
      {exclusions && exclusions.length > 0 && (
        <ul className='team-assistant-allowlist'>
          {exclusions.map((exclusion) => {
            const removing = busyKey === `remove:${exclusion.id}`;
            const label = channelLabel(exclusion);
            return (
              <li
                key={exclusion.id}
                className='team-assistant-allowlist-item'
                data-testid='team-assistant-channel-exclusion'
              >
                <div className='team-assistant-allowlist-info'>
                  <span className='team-assistant-name team-assistant-channel-name'>
                    {exclusion.channelKind === 'private_channel' ? (
                      <LockIcon title='Private channel' />
                    ) : (
                      <HashIcon />
                    )}
                    {label}
                  </span>
                </div>
                <button
                  type='button'
                  className='btn-secondary danger'
                  disabled={busy}
                  aria-label={`Remove #${label}`}
                  onClick={() => handleRemove(exclusion)}
                >
                  {removing ? '…' : 'Remove'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {picking ? (
        <ChannelPicker
          channels={available}
          error={availableError}
          excludedIds={excludedIds}
          adding={adding}
          disabled={disabled}
          onPick={handleAdd}
          onClose={() => setPicking(false)}
        />
      ) : (
        <div className='organisation-actions'>
          <button
            type='button'
            className='btn-primary'
            disabled={busy}
            onClick={openPicker}
          >
            Exclude channel
          </button>
        </div>
      )}
    </div>
  );
};
